import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, CreditCard, BookOpen, LogOut, Zap, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../contexts/AdminContext';
import { useSidebar } from '../contexts/SidebarContext';
import { formatPlanDate } from '../utils/planDate';
import UserAvatar from './ui/UserAvatar';
import DarkTooltip from './ui/DarkTooltip';

// Site de vendas (landing). Mesmo destino do CTA Upgrade do Sidebar.
const SALES_PRECOS_URL = 'https://darkplanner.com.br/precos';

/**
 * HeaderUserMenu — pill do plano + avatar com dropdown (estilo Fish Audio).
 *
 * Pill: [nome] [tag do plano] [avatar] dentro de um contorno arredondado;
 * hover → tooltip com a data de renovação/vencimento; clique → dropdown com
 * Meu Perfil / Meu Plano / Documentação da API / Encerrar sessão (+ Upgrade
 * para usuários free).
 *
 * Logout: dispara o evento global 'auth-logout' (mesmo padrão do 'auth-login'
 * já usado no App.jsx) — o App.jsx escuta e executa o handleLogout central.
 */
const HeaderUserMenu = ({ userData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { subscriptionData, isLoading } = useAdmin();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Fecha em click-outside e ESC — listeners só enquanto aberto.
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  // ── Plano (mesma lógica EIXO 2b do antigo selo do Sidebar) ──
  const sd = subscriptionData;
  const _capPlan = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const isPremium = !!sd && (sd.is_super_admin || sd.estado_efetivo === 'PREMIUM');
  const isFreeUser = !!sd && !sd.is_super_admin && sd.estado_efetivo !== 'PREMIUM';

  const planTagText = !sd
    ? null
    : sd.is_super_admin
      ? t('header.plan_tag_admin')
      : sd.estado_efetivo === 'PREMIUM'
        ? (_capPlan(sd.plano_contratado) || 'Pro').toUpperCase()
        : t('header.plan_tag_free');

  // Data de renovação/vencimento — vai pro tooltip da pill e pro cabeçalho do menu.
  const getRenewalDisplay = () => {
    if (!sd) return null;
    if (sd.is_super_admin) return t('sidebar.full_access');
    if (sd.estado_efetivo === 'PREMIUM') {
      if (sd.cancellation_requested) {
        const d = formatPlanDate(sd.end_date);
        return d ? t('sidebar.valid_until', { date: d }) : null;
      }
      const d = formatPlanDate(sd.next_charge_date || sd.end_date);
      return d ? t('sidebar.renews_on', { date: d }) : null;
    }
    if (sd.estado_efetivo === 'CANCELADO' || sd.estado_efetivo === 'REEMBOLSADO') {
      const plan = _capPlan(sd.plano_contratado);
      const d = formatPlanDate(sd.end_date);
      return d ? t('sidebar.plan_ended', { plan, date: d }) : t('sidebar.plan_ended_nodate', { plan });
    }
    return t('sidebar.plan_free');
  };
  const renewalText = getRenewalDisplay();

  const displayName = userData?.full_name
    || (userData?.username?.startsWith('USER_') ? userData?.email?.split('@')[0] : userData?.username);

  const closeAnd = (fn) => () => { setMenuOpen(false); fn(); };

  const handleLogout = () => {
    setMenuOpen(false);
    window.dispatchEvent(new Event('auth-logout'));
  };

  // ── Sub-blocos visuais ──
  const planTag = planTagText && !isLoading ? (
    <span style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      padding: '2px 7px',
      borderRadius: '9999px',
      whiteSpace: 'nowrap',
      fontFamily: 'var(--dp-font)',
      lineHeight: 1.4,
      ...(isPremium
        ? {
            color: 'var(--dp-blue-500, #0DAEFF)',
            background: 'var(--dp-blue-subtle, rgba(13,174,255,0.08))',
            border: '1px solid rgba(13, 174, 255, 0.25)',
          }
        : {
            color: 'var(--dp-text-secondary)',
            background: 'transparent',
            border: '1px solid var(--dp-border-medium)',
          }),
    }}>
      {planTagText}
    </span>
  ) : null;

  const menuItemStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: 'var(--dp-text-secondary)',
    fontSize: '12.5px',
    fontWeight: 500,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.15s, color 0.15s',
  };

  const itemHover = {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
      e.currentTarget.style.color = 'var(--dp-text-primary)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = 'var(--dp-text-secondary)';
    },
  };

  const separator = <div style={{ height: '1px', background: 'var(--dp-border-medium)', margin: '6px 4px' }} />;

  const avatarSize = isMobile ? 26 : 28;

  return (
    <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Pill — contorno que envolve nome + tag + avatar (padrão Fish) */}
      <DarkTooltip content={menuOpen ? '' : (renewalText || '')} position="bottom" maxWidth={260}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={t('header.user_menu')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '8px',
            padding: isMobile ? '2px 2px 2px 8px' : '3px 3px 3px 10px',
            background: menuOpen ? 'var(--dp-bg-card)' : 'transparent',
            border: `1px solid ${menuOpen ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)'}`,
            borderRadius: '9999px',
            cursor: 'pointer',
            fontFamily: 'var(--dp-font)',
            transition: 'border-color 0.2s, background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--dp-bg-card)';
            e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
          }}
          onMouseLeave={(e) => {
            if (!menuOpen) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
            }
          }}
        >
          {!isMobile && displayName && (
            <span style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--dp-text-primary)',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayName}
            </span>
          )}
          {planTag}
          <UserAvatar
            avatarUrl={userData?.avatar_url}
            name={displayName}
            size={avatarSize}
          />
        </button>
      </DarkTooltip>

      {/* Dropdown */}
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '260px',
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--dp-bg-elevated)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: 'var(--dp-radius-sm, 8px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            padding: '6px',
            zIndex: 100,
          }}
        >
          {/* Cabeçalho: nome + email + plano/data */}
          <div style={{ padding: '8px 10px 10px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayName}
            </div>
            {userData?.email && (
              <div style={{
                fontSize: '11px',
                color: 'var(--dp-text-tertiary)',
                fontFamily: 'var(--dp-font)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: '1px',
              }}>
                {userData.email}
              </div>
            )}
            {(planTag || renewalText) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '7px', minWidth: 0 }}>
                {planTag}
                {renewalText && (
                  <span style={{
                    fontSize: '10.5px',
                    color: 'var(--dp-text-tertiary)',
                    fontFamily: 'var(--dp-font)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {renewalText}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Upgrade — só para free (e nunca durante o loading do plano) */}
          {isFreeUser && !isLoading && (
            <a
              href={SALES_PRECOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                margin: '0 4px 4px',
                padding: '8px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'var(--dp-font)',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Zap size={13} />
              {t('sidebar.upgrade')}
            </a>
          )}

          {separator}

          <button role="menuitem" style={menuItemStyle} {...itemHover} onClick={closeAnd(() => navigate('/configuracoes'))}>
            <User size={14} /> {t('header.my_profile')}
          </button>
          <button role="menuitem" style={menuItemStyle} {...itemHover} onClick={closeAnd(() => navigate('/meu-plano'))}>
            <CreditCard size={14} /> {t('nav.my_plan')}
          </button>
          <button role="menuitem" style={menuItemStyle} {...itemHover} onClick={closeAnd(() => navigate('/central-de-ajuda'))}>
            <HelpCircle size={14} /> {t('header.help_center', 'Central de Ajuda')}
          </button>
          <button role="menuitem" style={menuItemStyle} {...itemHover} onClick={closeAnd(() => navigate('/api-docs'))}>
            <BookOpen size={14} /> {t('header.api_docs')}
          </button>

          {separator}

          <button
            role="menuitem"
            style={{ ...menuItemStyle, color: 'var(--dp-red-500, #F87171)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-red-bg, rgba(248,113,113,0.10))'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            onClick={handleLogout}
          >
            <LogOut size={14} /> {t('header.logout')}
          </button>
        </div>
      )}
    </div>
  );
};

export default HeaderUserMenu;
